import { performLogout } from 'src/utils/logout';

import { getAuthFromStorage } from 'src/lib/auth-storage';

// Default token expiration time in milliseconds (3 days)
const DEFAULT_EXPIRATION_TIME = 3 * 24 * 60 * 60 * 1000;

// Time before expiration to trigger refresh (1 hour)
const REFRESH_THRESHOLD = 60 * 60 * 1000;

/**
 * Adds token expiration information to local storage
 * @param expiresAt - Optional expiration timestamp from server response
 */
export const setTokenExpiration = (expiresAt?: Date | string): void => {
  try {
    const auth = getAuthFromStorage();
    if (!auth) return;

    let expiration: number;
    if (expiresAt) {
      expiration = new Date(expiresAt).getTime();
    } else {
      // Calculate expiration time - 3 days from now by default
      expiration = Date.now() + DEFAULT_EXPIRATION_TIME;
    }

    // Store expiration separately to avoid modifying the auth object structure
    localStorage.setItem('token_expires_at', expiration.toString());
  } catch (error) {
    console.error('Failed to set token expiration:', error);
  }
};

/**
 * Checks if the stored token has expired
 * @returns true if token has expired or doesn't exist, false otherwise
 */
export const isTokenExpired = (): boolean => {
  try {
    const auth = getAuthFromStorage();
    if (!auth || !auth.token) return true;

    const expiresAtStr = localStorage.getItem('token_expires_at');
    if (!expiresAtStr) return false; // No expiration set, assume valid

    const expiresAt = parseInt(expiresAtStr, 10);

    return Date.now() > expiresAt;
  } catch (error) {
    console.error('Error checking token expiration:', error);
    return true; // If there's an error, treat as expired
  }
};

/**
 * Checks if the token is about to expire (within 1 hour)
 * @returns true if token is about to expire
 */
export const isTokenAboutToExpire = (): boolean => {
  try {
    const expiresAtStr = localStorage.getItem('token_expires_at');
    if (!expiresAtStr) return false;

    const expiresAt = parseInt(expiresAtStr, 10);

    return Date.now() > expiresAt - REFRESH_THRESHOLD;
  } catch (error) {
    console.error('Error checking if token is about to expire:', error);
    return false;
  }
};

/**
 * Handle expired tokens by clearing auth storage
 * @returns true if token was expired and cleared
 */
export const handleExpiredToken = (): boolean => {
  if (isTokenExpired()) {
    console.log('Token has expired, clearing auth storage');
    performLogout(false); // Don't redirect here, let the calling component handle it
    return true;
  }
  return false;
};
